// deps
import React, { useEffect, useRef, useCallback, useMemo } from "react";
import cx from "classnames";

// helpers
import { withStyles } from "@core-ui/helpers/WithStyles";

// styles
import styles from "./HorizontalScroller.scss";

// hooks
import { useMountedRef } from "@core-ui/hooks/UseMountedRef";

type ModifiedElement = HTMLDivElement &
    IntersectionObserverEntry & {
        _elementIndex: number;
        target: {
            _elementIndex: number;
        };
    };

export interface HorizontalScrollerProps {
    containerClassName: string;
    height?: number;
    itemClassName: string;
    items: React.ReactNode[];
    itemVisibilityThreshold: number;
    width?: number;
    onItemVisible?: (elementIndex: number) => void;
}

const HorizontalScroller: React.FunctionComponent<HorizontalScrollerProps> = props => {
    const isMountedRef = useMountedRef();
    const {
        items,
        height,
        width,
        containerClassName,
        itemClassName,
        onItemVisible = (): void => void 0,
        itemVisibilityThreshold,
    } = props;
    const _items = items.map(_ => React.createRef<ModifiedElement>());
    const _io = useRef<IntersectionObserver | null>(null);

    const handleIntersectCallback = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (
                    isMountedRef.current &&
                    entry.isIntersecting &&
                    (entry.target as ModifiedElement)._elementIndex !== undefined
                ) {
                    onItemVisible((entry.target as ModifiedElement)._elementIndex);
                    if (_io.current != null) {
                        _io.current.unobserve(entry.target);
                    }
                }
            });
        },
        [isMountedRef, onItemVisible],
    );

    useEffect(() => {
        if (
            window !== undefined &&
            IntersectionObserver !== undefined &&
            _items.length > 0 &&
            onItemVisible !== undefined
        ) {
            _io.current = new IntersectionObserver(handleIntersectCallback, {
                root: null,
                rootMargin: "0px",
                threshold: [itemVisibilityThreshold],
            });

            _items.forEach((item, index) => {
                if (_io.current != null && item.current !== null) {
                    item.current._elementIndex = index;
                    _io.current.observe(item.current);
                }
            });
        }
    }, [_items, handleIntersectCallback, itemVisibilityThreshold, onItemVisible]);

    const style = useMemo(
        () => ({
            ...(height && { height: `${height}px` }),
            ...(width && { width: `${width}px` }),
        }),
        [height, width],
    );

    if (props.items.length === 0) {
        return null;
    }

    return (
        <div className={cx(styles.itemList, containerClassName)}>
            {items.map((item, index) => (
                <div style={style} className={cx(styles.item, itemClassName)} key={index} ref={_items[index]}>
                    {item}
                </div>
            ))}
        </div>
    );
};

export default withStyles(HorizontalScroller, styles);
